import React, { useCallback } from "react";
import axios from "axios";
import { useStyletron } from "baseui";
import { Input, StyledInput } from "baseui/input";
import { Tag, KIND } from "baseui/tag";
import { Button } from "baseui/button";
import { FormControl } from "baseui/form-control";
import { capitalizeFirstLetter } from "./utilities";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalButton,
  SIZE,
  ROLE,
} from "baseui/modal";

const InputReplacement = React.forwardRef(
  ({ tags, removeTag, ...restProps }, ref) => {
    const [css] = useStyletron();
    return (
      <div
        className={css({
          flex: "1 1 0%",
          flexWrap: "wrap",
          display: "flex",
          alignItems: "center",
        })}
      >
        {tags.map((tag, index) => (
          <Tag
            kind={KIND.primary}
            onActionClick={() => removeTag(tag)}
            key={index}
          >
            {tag}
          </Tag>
        ))}
        <StyledInput ref={ref} {...restProps} />
      </div>
    );
  }
);

export default function TagInput({
  setNewVideoTags,
  newVideoTags,
  availableTags,
  setAvailableTags,
}) {
  const [value, setValue] = React.useState("");
  const [editValue, setEditValue] = React.useState("");
  const [tags, setTags] = React.useState([]);
  const [editTags, setEditTags] = React.useState([]);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [remove, setRemove] = React.useState(false);

  console.log("received newVideoTags", newVideoTags);
  console.log("availableTags in 'TagInput'", availableTags);

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/list/tags`);
      setAvailableTags(response.data.tags.map((tag) => tag.name));
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  }, [apiUrl, setAvailableTags]);

  React.useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  React.useEffect(() => {
    if (newVideoTags && availableTags.length > 0) {
      // Filter to only include tags that exist in availableTags
      const selectedTags = newVideoTags.filter((tag) =>
        availableTags.includes(tag)
      );
      setTags(selectedTags);
    }
  }, [newVideoTags, availableTags]);

  // React.useEffect(() => {
  //   if (newVideoTags) {
  //     React.setAvailbleTags(newVideoTags);
  //   }
  // }, [newVideoTags]);

  const addTag = async (tag, origin) => {
    const lowerCaseTag = tag.toLowerCase();

    if (origin === "newVideoModal") {
      const matchedTag = availableTags.find(
        (availableTag) => availableTag.toLowerCase() === lowerCaseTag
      );

      if (matchedTag && !tags.includes(matchedTag)) {
        const newTags = [...tags, matchedTag];
        setTags(newTags);
        setNewVideoTags(newTags);
      }
    } else if (origin === "editTagsModal") {
      const matchedTag = availableTags.find(
        (availableTag) => availableTag.toLowerCase() === lowerCaseTag
      );
      if (matchedTag && !editTags.includes(matchedTag)) {
        const newTags = [...editTags, matchedTag];
        setEditTags(newTags);
        setRemove(true);
      } else {
        setRemove(false);

        await axios.post(`${apiUrl}/api/add/tags`, {
          tag: capitalizeFirstLetter(editValue),
        });
        fetchTags();
      }
    }
  };

  const removeTag = async (tag, origin, removeFromAvailableTags = false) => {
    console.log("Removing tag:", tag); // Log the tag value to ensure it's the correct tag

    if (origin === "newVideoModal") {
      const updatedTags = tags.filter(
        (t) => t.toLowerCase() !== tag.toLowerCase()
      );
      setTags(updatedTags);
      setNewVideoTags(updatedTags);
    } else if (origin === "editTagsModal" && !removeFromAvailableTags) {
      const updatedTags = editTags.filter(
        (t) => t.toLowerCase() !== tag.toLowerCase()
      );
      setEditTags(updatedTags);

      if (updatedTags.length === 0) {
        setRemove(false);
      }
    } else if (origin === "editTagsModal" && removeFromAvailableTags) {
      try {
        // Log the data sent to the delete request
        console.log("Attempting to delete tag from backend:", tag);

        const response = await axios.delete(`${apiUrl}/api/delete/tags`, {
          data: { tag },
        });

        console.log("Delete response:", response.data);
        fetchTags(); // Refresh the tags from the backend

        // Set editTags to reflect the deletion
        const updatedTags = editTags.filter(
          (t) => t.toLowerCase() !== tag.toLowerCase()
        );
        setEditTags(updatedTags);

        if (updatedTags.length === 0) {
          setRemove(false);
        }
      } catch (error) {
        console.error(
          "Error deleting tag:",
          error.response ? error.response.data : error.message
        );
        alert("Error deleting tag.");
      }
    }
  };

  const handleKeyDown = (event, origin) => {
    if (origin === "newVideoModal") {
      if (event.keyCode === 13 && !value) {
        event.preventDefault();
      }

      if (event.keyCode === 13 && value) {
        event.preventDefault();
        addTag(value, origin);
        setValue("");
      } else if (event.keyCode === 8 && !value && tags.length) {
        removeTag(tags[tags.length - 1], origin);
      }
    } else if (origin === "editTagsModal") {
      if (event.keyCode === 13 && editValue) {
        addTag(editValue, origin);
        setEditValue("");
      } else if (event.keyCode === 8 && !editValue && editTags.length) {
        removeTag(editTags[editTags.length - 1], origin);
      }
    }
  };

  const addNewTagBtn = async () => {
    if (editValue && !availableTags.includes(editValue)) {
      await axios.post(`${apiUrl}/api/add/tags`, {
        tag: capitalizeFirstLetter(editValue),
      });
      fetchTags();
      setIsModalOpen(false);
    }
  };

  const handleModalShow = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsModalOpen(true);
  };

  return (
    <div>
      <div style={{ marginBottom: "16px" }}>
        {availableTags.map((tag) => (
          <Button
            key={tag}
            onClick={() => addTag(tag, "newVideoModal")}
            disabled={tags.includes(tag)}
            type="button"
            style={{
              marginRight: ".25rem",
              marginTop: ".25rem",
              backgroundColor: "#bad8eb",
              color: "black",
            }}
          >
            {tag}
          </Button>
        ))}
      </div>

      <Input
        placeholder={tags.length ? "" : "Enter A Tag"}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={(e) => handleKeyDown(e, "newVideoModal")}
        overrides={{
          Input: {
            style: { width: "auto", flexGrow: 1 },
            component: InputReplacement,
            props: {
              tags: tags,
              removeTag: (tag) => removeTag(tag, "newVideoModal"),
            },
          },
        }}
      />
      <Button
        style={{
          marginTop: "1rem",
          backgroundColor: "#B8C5CD",
          color: "black",
        }}
        type="button"
        onClick={handleModalShow}
      >
        Create/Delete a Tag
      </Button>

      <Modal
        onClose={() => setIsModalOpen(false)}
        closeable
        isOpen={isModalOpen}
        animate
        autoFocus
        size={SIZE.default}
        role={ROLE.dialog}
      >
        <ModalHeader>Create/Delete a Tag</ModalHeader>
        <ModalBody>
          <div>
            {availableTags.map((currentTag) => (
              <Button
                key={currentTag}
                onClick={() => addTag(currentTag, "editTagsModal")}
                disabled={editTags.includes(currentTag)}
                type="button"
                style={{
                  marginRight: ".25rem",
                  marginTop: ".25rem",
                  backgroundColor: "#B8C5CD",
                  color: "black",
                }}
              >
                {currentTag}
              </Button>
            ))}
          </div>
          <FormControl label="Tag Name">
            <Input
              placeholder={
                editTags.length ? "" : "Enter a Tag to Create or Remove"
              }
              value={editValue}
              onChange={(e) => setEditValue(e.currentTarget.value)}
              onKeyDown={(e) => handleKeyDown(e, "editTagsModal")}
              overrides={{
                Input: {
                  style: { width: "auto", flexGrow: 1 },
                  component: InputReplacement,
                  props: {
                    tags: editTags,
                    removeTag: (tag) => removeTag(tag, "editTagsModal"),
                  },
                },
              }}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="tertiary" onClick={() => setIsModalOpen(false)}>
            Cancel
          </ModalButton>
          {remove ? (
            // <ModalButton onClick={() => removeTag(null, "editTagsModal", true)}>
            //   Remove Selected {editTags.length > 1 ? "Tags" : "Tag"}
            // </ModalButton>
            <ModalButton
              onClick={() =>
                editTags.forEach((tag) => removeTag(tag, "editTagsModal", true))
              }
            >
              Remove Selected {editTags.length > 1 ? "Tags" : "Tag"}
            </ModalButton>
          ) : (
            <ModalButton onClick={addNewTagBtn}>Create New Tag</ModalButton>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}
